import { useEffect } from "react";

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

/**
 * Single-key shortcuts (e.g. "j", "?", "Escape"), ignored while typing in a field, with a modifier held,
 * or while a dialog is open (dialogs handle their own keys).
 */
export function useShortcuts(keys: Record<string, () => void>, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey || event.defaultPrevented) return;
      if (isTypingTarget(event.target) || document.querySelector('[role="dialog"]')) return;
      const handler = keys[event.key];
      if (handler) {
        event.preventDefault();
        handler();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [keys, enabled]);
}

/** Move focus to the next (+1) or previous (-1) of `links`, starting from the focused one. */
export function stepFocus(links: (HTMLElement | null)[], step: 1 | -1): void {
  const present = links.filter((link): link is HTMLElement => link !== null);
  if (present.length === 0) return;
  const current = present.findIndex((link) => link === document.activeElement);
  const target = current === -1 ? (step === 1 ? 0 : present.length - 1) : current + step;
  const next = present[Math.min(present.length - 1, Math.max(0, target))];
  next?.focus();
  next?.scrollIntoView({ block: "nearest" });
}
